import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { Row, Col, Button, Badge } from 'reactstrap';

import { formatDate } from '../../utils';
import { showError } from '../../actions/notificationActions';
import { setPendingApprovalsCount } from '../../actions/approvalActions';
import approvals from '../../services/approvals.service';

import FilterBuilder from '../FilterBuilder';
import ApprovalStatusBadge from '../ApprovalStatusBadge';
import ViewResourceLink from '../ViewResourceLink';
import { approvalFields } from './constants';
import { serializeWhere } from './serializeWhere';
import { loadActionVocabulary } from './actionVocabulary';
import DataTable, { PageHeader, EmptyState } from '../DataTable';

const POLL_INTERVAL = 20000;
const PAGE_SIZE = 30;

// Ceiling for the sidebar badge's own count. The queue is keyset-paginated and
// exposes no total, so the badge is a floor ("at least this many"), not an exact
// figure — enough to nudge a checker, which is all it was ever for.
const BADGE_SCAN_LIMIT = 100;

const safeDate = (d) => (d ? formatDate(d) : '—');

// ─── URL <-> filter state helpers (matches Payments/List.js convention) ───────

function encodeConditions(conditions) {
  return encodeURIComponent(
    JSON.stringify(conditions.map(({ id, ...rest }) => rest))
  );
}

function parseUrlParams(search) {
  const params = new URLSearchParams(search);
  let conditions = null;
  let logicalOp = '$and';

  const raw = params.get('c');
  if (raw) {
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      conditions = parsed.map((c, i) => ({ ...c, id: Date.now() + i }));
    } catch (_) {}
  }

  const op = params.get('op');
  if (op === '$and' || op === '$or') logicalOp = op;

  return { conditions, logicalOp };
}

// The queue's landing view, preserved from the pre-FilterBuilder page: checkers
// arrive to the work that is actually waiting on them. Seeded only when the URL
// carries no filter state, and clearable like any other condition.
function defaultConditions() {
  return [
    {
      id: Date.now(),
      field: 'status',
      fieldType: 'enum',
      operator: '$eq',
      value: 'pending',
    },
  ];
}

class ApprovalsList extends Component {
  constructor(props) {
    super(props);
    const { conditions, logicalOp } = parseUrlParams(
      props.location ? props.location.search : ''
    );
    this.state = {
      rows: [], // accumulated across keyset pages
      conditions: conditions || defaultConditions(),
      logicalOp,
      // Rows come back newest first; pagination is forward-only via
      // meta.next_cursor. There is no total and no page number by design.
      nextCursor: null,
      hasMore: false,
      pagesLoaded: 0,
      // Starts true: componentDidMount always loads, and a first render that
      // said "no approval requests yet" would flash the wrong answer.
      loading: true, // initial / reset load
      loadingMore: false, // appending the next page
      error: null,
      actionOptions: [],
      actionLabels: {},
      builderKey: 0,
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.syncUrl(this.state.conditions, this.state.logicalOp);
    this.fetchActionVocabulary();
    this.load();
    this.refreshPendingCount();
    this.timer = setInterval(() => {
      this.pollRefresh();
      this.refreshPendingCount();
    }, POLL_INTERVAL);
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.timer) clearInterval(this.timer);
  }

  // The action vocabulary is used twice: as the options of the action filter,
  // and as the action -> label lookup for the table column.
  fetchActionVocabulary = () => {
    loadActionVocabulary()
      .then(({ options, labels }) => {
        if (!this.mounted) return;
        this.setState({ actionOptions: options, actionLabels: labels });
      })
      .catch(() => {
        // Non-fatal: the action filter degrades to a free-text input and the
        // table falls back to raw action keys. Not worth a toast over.
      });
  };

  syncUrl = (conditions, logicalOp) => {
    const params = new URLSearchParams();
    if (conditions.length > 0) {
      params.set('c', encodeConditions(conditions));
      params.set('op', logicalOp);
    }
    // The cursor is intentionally NOT URL-persisted — keyset cursors are opaque
    // row ids anchored to one result set, not shareable page numbers.
    const search = params.toString();
    const current = this.props.location
      ? this.props.location.search.replace(/^\?/, '')
      : '';
    if (search !== current) {
      this.props.history.replace({ search: search ? `?${search}` : '' });
    }
  };

  // Arguments for one keyset page. The same `where` + `limit` ride along on
  // every page fetch; only the cursor changes. `where` is omitted entirely when
  // no filter is active.
  pageArgs = (cursor) => {
    const { conditions, logicalOp } = this.state;
    return {
      where: serializeWhere(conditions, logicalOp),
      limit: PAGE_SIZE,
      cursor,
    };
  };

  // Every fetch is stamped with the walk it belongs to. A filter change (or a
  // manual refresh) starts a new walk, so a response from the previous one —
  // still in flight, or a poll fired just before the change — is discarded
  // rather than allowed to overwrite the new result set.
  walk = 0;

  isCurrentWalk = (walk) => this.mounted && walk === this.walk;

  // Initial / reset load: no cursor, replaces the accumulated rows.
  load = () => {
    const walk = ++this.walk;
    this.setState({
      loading: true,
      error: null,
      rows: [],
      nextCursor: null,
      hasMore: false,
      pagesLoaded: 0,
    });
    approvals
      .list(this.pageArgs(null))
      .then(({ data = [], meta = {} }) => {
        if (!this.isCurrentWalk(walk)) return;
        this.setState({
          loading: false,
          rows: data,
          nextCursor: meta.next_cursor != null ? meta.next_cursor : null,
          hasMore: !!meta.has_more,
          pagesLoaded: 1,
        });
      })
      .catch((err) => {
        if (!this.isCurrentWalk(walk)) return;
        this.setState({
          loading: false,
          error: (err && err.message) || 'Unable to load approval requests.',
        });
        this.props.dispatch(showError(err));
      });
  };

  // Background refresh of the queue. Only refreshes while the operator is still
  // on the first page — once they have paged further, silently replacing the
  // rows would yank away everything they loaded. The Refresh button always
  // works, and deliberately restarts the walk from page one.
  pollRefresh = () => {
    const { pagesLoaded, loading, loadingMore } = this.state;
    if (loading || loadingMore || pagesLoaded !== 1) return;

    const walk = this.walk;
    approvals
      .list(this.pageArgs(null))
      .then(({ data = [], meta = {} }) => {
        // Still the same walk, and the operator has not paged on since.
        if (!this.isCurrentWalk(walk) || this.state.pagesLoaded !== 1) return;
        this.setState({
          rows: data,
          nextCursor: meta.next_cursor != null ? meta.next_cursor : null,
          hasMore: !!meta.has_more,
        });
      })
      .catch(() => {
        // Silent: a failed background poll leaves the current rows alone.
      });
  };

  // Append the next keyset page using meta.next_cursor.
  loadMore = () => {
    const { nextCursor, hasMore, loadingMore, loading } = this.state;
    if (loading || loadingMore || !hasMore || nextCursor == null) return;

    const walk = this.walk;
    this.setState({ loadingMore: true });
    approvals
      .list(this.pageArgs(nextCursor))
      .then(({ data = [], meta = {} }) => {
        if (!this.isCurrentWalk(walk)) return;
        this.setState((prev) => ({
          loadingMore: false,
          rows: prev.rows.concat(data),
          nextCursor: meta.next_cursor != null ? meta.next_cursor : null,
          hasMore: !!meta.has_more,
          pagesLoaded: prev.pagesLoaded + 1,
        }));
      })
      .catch((err) => {
        // Keep the rows already loaded; just surface the error as a toast.
        if (!this.isCurrentWalk(walk)) return;
        this.setState({ loadingMore: false });
        this.props.dispatch(showError(err));
      });
  };

  // Keep the sidebar badge accurate regardless of the active filter.
  refreshPendingCount = () => {
    approvals
      .list({ where: { status: 'pending' }, limit: BADGE_SCAN_LIMIT })
      .then((res) => {
        const items = (res && res.data) || [];
        const actionable = items.filter((r) => r.can_check).length;
        this.props.dispatch(
          setPendingApprovalsCount(actionable || items.length)
        );
      })
      .catch(() => {
        /* silent — don't disrupt the console over a badge refresh */
      });
  };

  // Any filter change starts a fresh walk: rows and cursor are dropped and page
  // one is re-requested. A cursor is never carried across a filter change.
  applyConditions = (conditions, logicalOp, { reseedBuilder } = {}) => {
    this.setState(
      (prev) => ({
        conditions,
        logicalOp,
        builderKey: reseedBuilder ? prev.builderKey + 1 : prev.builderKey,
      }),
      () => {
        this.syncUrl(conditions, logicalOp);
        this.load();
      }
    );
  };

  handleFilterChange = (_where, conditions, logicalOp) => {
    this.applyConditions(conditions, logicalOp);
  };

  // "Only mine" is not a bespoke param any more — it is the ordinary clause
  // { maker_id: <current user id> }, added to and removed from the builder like
  // any other condition.
  toggleMine = () => {
    const { conditions, logicalOp } = this.state;
    const userId = this.props.user && this.props.user.id;
    if (userId == null) return;

    const next = this.isMineActive()
      ? conditions.filter((c) => !this.isMineCondition(c))
      : conditions.concat({
          id: Date.now(),
          field: 'maker_id',
          fieldType: 'number',
          operator: '$eq',
          value: userId,
        });

    this.applyConditions(next, logicalOp, { reseedBuilder: true });
  };

  isMineCondition = (c) => {
    const userId = this.props.user && this.props.user.id;
    return (
      c.field === 'maker_id' &&
      c.operator === '$eq' &&
      String(c.value) === String(userId)
    );
  };

  isMineActive = () => this.state.conditions.some(this.isMineCondition);

  render() {
    const {
      rows,
      conditions,
      logicalOp,
      loading,
      loadingMore,
      hasMore,
      error,
      actionOptions,
      actionLabels,
      builderKey,
    } = this.state;

    const { user } = this.props;
    const loaded = !loading && !error;
    const hasFilter = conditions.length > 0;
    const mineActive = this.isMineActive();

    return (
      <div className="container-fluid">
        <Row className="mt-3 mt-md-4">
          <Col xs={12}>
            <PageHeader
              title="Approvals"
              subtitle={
                <>
                  Requests awaiting a second approver
                  {rows.length > 0 &&
                    ` — ${rows.length} loaded${
                      hasMore ? ' (more available)' : ''
                    }`}
                </>
              }
            >
              {user && user.id != null && (
                <Button
                  size="sm"
                  color="primary"
                  outline={!mineActive}
                  onClick={this.toggleMine}
                  title="Filter to requests you raised"
                >
                  Only mine
                </Button>
              )}
              <Button
                size="sm"
                color="secondary"
                outline
                onClick={this.load}
                disabled={loading}
              >
                {loading ? 'Refreshing…' : 'Refresh'}
              </Button>
            </PageHeader>

            <div style={{ margin: '8px 0 16px' }}>
              <FilterBuilder
                key={builderKey}
                fields={approvalFields(actionOptions)}
                initialConditions={conditions}
                initialLogicalOp={logicalOp}
                onChange={this.handleFilterChange}
              />
            </div>

            <DataTable
              minWidth={1000}
              loading={loading}
              footer={
                /* Forward-only keyset pagination: append the next page via
                   meta.next_cursor. No total and no page number — the API
                   has neither. */
                rows.length > 0 && !loading && hasMore ? (
                  <div className="text-center">
                    <Button
                      color="primary"
                      outline
                      size="sm"
                      onClick={this.loadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                  </div>
                ) : null
              }
            >
              <thead className="text-primary">
                <tr>
                  <th>Title</th>
                  <th>Action</th>
                  <th>Maker</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Resource</th>
                  <th />
                </tr>
              </thead>
              {error && !loading ? (
                <EmptyState
                  colSpan={8}
                  icon={false}
                  heading="Something went wrong"
                  message={error}
                  actionLabel="Retry"
                  onAction={this.load}
                />
              ) : loaded && rows.length === 0 ? (
                <EmptyState
                  colSpan={8}
                  icon={false}
                  heading={
                    hasFilter
                      ? 'No approval requests match these filters.'
                      : 'No approval requests yet.'
                  }
                />
              ) : (
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.reference}>
                      <td>{r.title || '—'}</td>
                      <td title={r.action}>
                        {actionLabels[r.action] || r.action}
                      </td>
                      <td>{r.maker ? r.maker.name : '—'}</td>
                      <td>
                        <ApprovalStatusBadge status={r.status} />
                        {r.otp_required && (
                          <Badge color="info" className="ml-1">
                            OTP
                          </Badge>
                        )}
                      </td>
                      <td>{safeDate(r.created_at)}</td>
                      <td>{safeDate(r.expires_at)}</td>
                      <td>
                        <ViewResourceLink uri={r.resource_uri} />
                      </td>
                      <td>
                        {/* `can_check` is read straight off the item —
                            never recomputed client-side. Only rows the
                            viewer may decide offer Approve/Deny; the
                            rest are read-only. */}
                        <Link
                          to={`/approvals/${r.reference}`}
                          className={`btn btn-sm ${
                            r.can_check
                              ? 'btn-primary'
                              : 'btn-outline-secondary'
                          }`}
                        >
                          {r.can_check ? 'Approve / Deny' : 'View'}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </DataTable>
          </Col>
        </Row>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(ApprovalsList);
